// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { color } from "@plumberssupply/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: CustomAppState) => ({
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

interface UseStandardCheckoutInsteadStyles {
    container?: InjectableCss;
    description?: TypographyProps;
    button?: ButtonPresentationProps;
}

const useStandardCheckoutInsteadStyles: UseStandardCheckoutInsteadStyles = {
    container: {
        css: css`
            margin-top: 20px;
            padding: 16px;
            background-color: ${color("common.ui12")};
        `,
    },
    description: {
        size: 12,
        lineHeight: "18px",
        css: css`
            display: block;
        `,
    },
    button: {
        sizeVariant: "small",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 12px;
            &,
            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                background: transparent;
            }
        `,
    },
};

const styles = useStandardCheckoutInsteadStyles;

const UseStandardCheckoutInstead: FC<Props> = ({ cartPageUrl }) => {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const history = useHistory();

    return (
        <>
            <StyledWrapper {...styles.container}>
                <Typography {...styles.description}>
                    {translate("Need more options with managing your order? Visit our standard checkout process.")}
                </Typography>
                <Button {...styles.button} onClick={() => setShowConfirmationModal(true)}>
                    {translate("Use Standard Checkout")}
                </Button>
            </StyledWrapper>
            <TwoButtonModal
                headlineText={translate("Use Standard Checkout")}
                modalIsOpen={showConfirmationModal}
                messageText={siteMessage("Use_Standard_Checkout_Instead")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Go")}
                onCancel={() => setShowConfirmationModal(false)}
                onSubmit={() => {
                    history.push(cartPageUrl!);
                }}
            ></TwoButtonModal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(UseStandardCheckoutInstead),
    definition: {
        displayName: "Use Standard Checkout Instead",
        group: "Checkout - Review & Submit",
        allowedContexts: ["ExpressCheckoutPage"] as any,
    },
};
export default widgetModule;
